/**
 * Konten editorial per hari penting. Slug yang punya konten kaya = lebih
 * unik untuk SEO. Slug tanpa entry tetap tampil dengan template auto-generated
 * (nama, tanggal, tahun-tahun, scope, related) yang juga unik per slug.
 */

export interface HariContent {
  /** Subtitle pendek untuk hero & meta description. */
  tagline: string;
  /** 1 kalimat ringkas untuk featured snippet. */
  quickAnswer: string;
  /** Latar belakang/sejarah singkat (paragraf). */
  history: string;
  /** Makna/poin penting (paragraf opsional). */
  meaning?: string;
  /** Cara memperingati / tradisi (bullet). */
  traditions?: string[];
  /** Tokoh kunci (mis. RA Kartini). */
  figure?: { name: string; role: string; bio?: string };
  /** Slug terkait. */
  relatedSlugs?: string[];
  /** FAQ tambahan (slug akan otomatis dapat 3 FAQ baseline). */
  extraFaq?: { q: string; a: string }[];
  /** Kata-kata ucapan siap copy untuk dibagikan ke media sosial. */
  greetings?: string[];
  /** Pictogram unik untuk FeaturedSvg. Lihat IconKey di FeaturedSvg.tsx. */
  iconKey?:
    | "kartini" | "guru" | "ibu" | "ayah"
    | "buruh" | "batik" | "pahlawan" | "kemerdekaan"
    | "valentine" | "natal" | "sumpah" | "anak";
}

export const HARI_CONTENT: Record<string, HariContent> = {
  buruh: {
    tagline: "Peringatan global untuk hak-hak pekerja, ditetapkan libur nasional di Indonesia.",
    quickAnswer: "Hari Buruh Internasional diperingati setiap tanggal 1 Mei dan merupakan hari libur nasional di Indonesia sejak 2014.",
    history:
      "Hari Buruh atau May Day berakar dari aksi pekerja di Chicago tahun 1886 yang menuntut delapan jam kerja per hari. Kongres Buruh Internasional di Paris (1889) menetapkan 1 Mei sebagai Hari Buruh Sedunia. Di Indonesia, peringatan ini sempat ditiadakan pada masa Orde Baru dan baru kembali diakui sebagai libur resmi melalui Keppres No. 24 Tahun 2013 yang berlaku sejak 1 Mei 2014.",
    meaning:
      "Hari Buruh menjadi momentum menyuarakan kesejahteraan, upah layak, jaminan sosial, serta perlindungan kerja bagi seluruh pekerja Indonesia.",
    traditions: [
      "Aksi damai serikat pekerja di kota-kota besar.",
      "Diskusi publik tentang kebijakan ketenagakerjaan.",
      "Acara sosial perusahaan untuk apresiasi karyawan.",
    ],
    relatedSlugs: ["pekerja-nasional", "kemerdekaan", "pancasila"],
    iconKey: "buruh",
    greetings: [
      "Selamat Hari Buruh! Terima kasih atas kerja keras seluruh pekerja Indonesia.",
      "Hari Buruh 1 Mei: semoga upah layak, jaminan sosial, dan keadilan kerja makin nyata.",
      "Salam hangat untuk semua pekerja - tanpamu, roda bangsa tak akan berputar.",
      "Selamat May Day! Mari bersatu untuk hak dan kesejahteraan bersama.",
      "Bekerja keras, hidup layak. Selamat Hari Buruh Internasional!",
    ],
  },
  guru: {
    tagline: "Penghormatan kepada guru se-Indonesia, bertepatan dengan HUT PGRI.",
    quickAnswer: "Hari Guru Nasional diperingati setiap tanggal 25 November, bersamaan dengan hari lahir Persatuan Guru Republik Indonesia (PGRI).",
    history:
      "Hari Guru Nasional ditetapkan melalui Keppres No. 78 Tahun 1994. Tanggal 25 November dipilih karena bertepatan dengan berdirinya PGRI pada 25 November 1945, hanya 100 hari setelah Proklamasi Kemerdekaan. Peringatan ini menjadi bentuk penghargaan atas peran guru sebagai pilar pendidikan bangsa.",
    meaning:
      "Guru bukan hanya pengajar, tetapi pembentuk karakter generasi penerus. Tema peringatan tiap tahun berubah mengikuti prioritas pendidikan nasional.",
    traditions: [
      "Upacara bendera di sekolah-sekolah seluruh Indonesia.",
      "Pemberian penghargaan kepada guru berprestasi & guru daerah 3T.",
      "Apresiasi murid kepada guru dengan kartu, bunga, atau pertunjukan.",
    ],
    relatedSlugs: ["pendidikan-nasional", "kartini", "anak-nasional"],
    iconKey: "guru",
    greetings: [
      "Selamat Hari Guru! Terima kasih telah menjadi pelita bagi generasi penerus bangsa.",
      "Tanpa jasamu, kami tak akan sampai di sini. Selamat Hari Guru Nasional.",
      "Guru: digugu lan ditiru. Hormat kami untuk semua guru Indonesia.",
      "Selamat Hari Guru 25 November - semoga sehat dan tetap menginspirasi.",
      "Pahlawan tanpa tanda jasa, kau pelita dalam gulita. Terima kasih, Guru.",
    ],
  },
  ibu: {
    tagline: "Penghormatan untuk peran ibu, lahir dari Kongres Perempuan Indonesia 1928.",
    quickAnswer: "Hari Ibu di Indonesia diperingati setiap tanggal 22 Desember, berbeda dari Mother's Day internasional di bulan Mei.",
    history:
      "Hari Ibu ditetapkan oleh Presiden Soekarno melalui Keppres No. 316 Tahun 1959. Tanggal 22 Desember dipilih untuk mengenang Kongres Perempuan Indonesia I yang berlangsung di Yogyakarta pada 22-25 Desember 1928 - tonggak gerakan perempuan Indonesia memperjuangkan kemerdekaan dan kesetaraan.",
    meaning:
      "Berbeda dengan Mother's Day yang fokus pada peran domestik, Hari Ibu Indonesia menekankan kebangkitan, persatuan, dan kontribusi perempuan dalam pembangunan bangsa.",
    traditions: [
      "Memberikan hadiah, bunga, atau ucapan kepada ibu.",
      "Upacara peringatan di kantor pemerintahan & organisasi perempuan.",
      "Pengakuan publik untuk perempuan inspiratif di berbagai bidang.",
    ],
    relatedSlugs: ["kartini", "perempuan-internasional", "ayah"],
    iconKey: "ibu",
    greetings: [
      "Selamat Hari Ibu! Cintamu adalah rumah pertama dan ternyaman.",
      "Untuk Ibu, kasihmu sepanjang masa. Terima kasih, Bu.",
      "Hari Ibu 22 Desember: doa terbaik untuk semua ibu di Indonesia.",
      "Tak ada cinta sebesar cinta seorang ibu. Selamat Hari Ibu.",
      "Ibu, kau sumber kekuatanku. Selamat Hari Ibu, sehat selalu.",
    ],
  },
  ayah: {
    tagline: "Apresiasi untuk peran ayah, dideklarasikan di Solo tahun 2006.",
    quickAnswer: "Hari Ayah Nasional di Indonesia diperingati setiap tanggal 12 November.",
    history:
      "Hari Ayah Nasional dideklarasikan oleh Paguyuban Satu Hati di Solo pada 12 November 2006. Berbeda dengan Father's Day di banyak negara yang dirayakan Juni, Indonesia memilih tanggal sendiri untuk menonjolkan nilai keluarga khas Nusantara. Meski belum ada Keppres resmi, peringatannya makin meluas tiap tahun.",
    meaning:
      "Hari Ayah mengingatkan pentingnya peran ayah dalam pengasuhan dan keseimbangan tanggung jawab keluarga.",
    traditions: [
      "Memberi ucapan, hadiah, atau makan bersama ayah.",
      "Tulisan apresiasi di media sosial.",
      "Kegiatan komunitas tentang fatherhood positif.",
    ],
    relatedSlugs: ["ibu", "anak-nasional", "kemerdekaan"],
    iconKey: "ayah",
    greetings: [
      "Selamat Hari Ayah! Punggungmu adalah tempat ternyaman untuk bersandar.",
      "Ayah - pahlawan tanpa cape, sahabat tanpa pamrih. Selamat Hari Ayah.",
      "Untuk ayah terbaik di dunia: terima kasih untuk segalanya.",
      "Hari Ayah 12 November: semoga selalu sehat dan diberkahi, Pak.",
      "Cintamu sederhana tapi dalam, Yah. Selamat Hari Ayah Nasional.",
    ],
  },
  kartini: {
    tagline: "Mengenang Raden Adjeng Kartini, pelopor emansipasi perempuan Indonesia.",
    quickAnswer: "Hari Kartini diperingati setiap tanggal 21 April, bertepatan dengan hari kelahiran RA Kartini pada 21 April 1879.",
    history:
      "Presiden Soekarno menetapkan Hari Kartini melalui Keppres No. 108 Tahun 1964 yang sekaligus mengakui RA Kartini sebagai Pahlawan Kemerdekaan Nasional. Surat-surat Kartini yang kemudian diterbitkan dengan judul 'Habis Gelap Terbitlah Terang' menjadi inspirasi gerakan perempuan Indonesia.",
    meaning:
      "Hari Kartini menjadi simbol perjuangan kesetaraan akses pendidikan dan emansipasi perempuan.",
    figure: {
      name: "Raden Adjeng Kartini",
      role: "Pendidik & pelopor emansipasi perempuan",
      bio: "Lahir 21 April 1879 di Jepara, wafat 17 September 1904. Mendirikan sekolah gadis di Jepara dan Rembang. Surat-suratnya kepada sahabat Belanda dibukukan sebagai 'Door Duisternis tot Licht'.",
    },
    traditions: [
      "Mengenakan busana kebaya dan pakaian daerah di sekolah & kantor.",
      "Lomba pidato, puisi, dan menulis surat ala Kartini.",
      "Diskusi tentang isu kesetaraan gender di Indonesia.",
    ],
    relatedSlugs: ["pendidikan-nasional", "ibu", "perempuan-internasional"],
    iconKey: "kartini",
    greetings: [
      "Selamat Hari Kartini! Habis gelap terbitlah terang.",
      "Berani bermimpi, berani bersuara - selamat Hari Kartini, perempuan Indonesia!",
      "Untuk semua perempuan tangguh: kalian Kartini masa kini.",
      "21 April: kenang RA Kartini, lanjutkan perjuangannya.",
      "Selamat Hari Kartini! Setara dalam pendidikan, setara dalam kesempatan.",
    ],
  },
  "batik-nasional": {
    tagline: "Memperingati pengakuan UNESCO atas batik sebagai warisan budaya Indonesia.",
    quickAnswer: "Hari Batik Nasional diperingati setiap tanggal 2 Oktober.",
    history:
      "Pada 2 Oktober 2009, UNESCO menetapkan batik Indonesia sebagai Masterpiece of the Oral and Intangible Heritage of Humanity. Sebagai respons, Presiden Susilo Bambang Yudhoyono menerbitkan Keppres No. 33 Tahun 2009 yang menetapkan tanggal tersebut sebagai Hari Batik Nasional.",
    meaning:
      "Hari Batik mendorong masyarakat Indonesia mengenakan batik dan melestarikan teknik membatik tradisional dari berbagai daerah.",
    traditions: [
      "Mengenakan batik di kantor, sekolah, & ruang publik.",
      "Pameran batik dari berbagai daerah Nusantara.",
      "Workshop membatik untuk pelajar dan komunitas.",
    ],
    relatedSlugs: ["kemerdekaan", "sumpah-pemuda", "kebangkitan-nasional"],
    iconKey: "batik",
    greetings: [
      "Selamat Hari Batik Nasional! Yuk bangga pakai batik Indonesia.",
      "2 Oktober: rayakan warisan dunia milik kita - batik Nusantara.",
      "Setiap motif punya cerita. Selamat Hari Batik Nasional!",
      "Batik bukan sekadar kain, tapi identitas bangsa. Selamat Hari Batik!",
      "Pakai batik hari ini, lestarikan budaya nanti.",
    ],
  },
  pahlawan: {
    tagline: "Mengenang pertempuran 10 November 1945 di Surabaya.",
    quickAnswer: "Hari Pahlawan diperingati setiap tanggal 10 November.",
    history:
      "Hari Pahlawan ditetapkan melalui Keppres No. 316 Tahun 1959 untuk mengenang Pertempuran Surabaya 10 November 1945, perlawanan rakyat Indonesia melawan tentara Sekutu/Inggris yang menewaskan ribuan pejuang dan penduduk sipil.",
    meaning:
      "Hari Pahlawan menghormati seluruh pejuang yang gugur demi kemerdekaan dan keutuhan NKRI.",
    traditions: [
      "Upacara di Taman Makam Pahlawan.",
      "Pemberian gelar Pahlawan Nasional kepada tokoh baru.",
      "Mengheningkan cipta selama 60 detik secara serentak.",
    ],
    relatedSlugs: ["kemerdekaan", "kebangkitan-nasional", "sumpah-pemuda"],
    iconKey: "pahlawan",
    greetings: [
      "Selamat Hari Pahlawan! Jasa kalian abadi dalam kemerdekaan kami.",
      "10 November: hormat untuk pahlawan Surabaya dan seluruh Indonesia.",
      "Berkat pengorbananmu, kami merdeka. Terima kasih, pahlawanku.",
      "Pahlawan sejati tak butuh tanda jasa - hanya kenangan dalam hati bangsa.",
      "Selamat Hari Pahlawan Nasional. Merdeka!",
    ],
  },
  kemerdekaan: {
    tagline: "Hari Proklamasi Kemerdekaan Republik Indonesia.",
    quickAnswer: "Hari Kemerdekaan RI diperingati setiap tanggal 17 Agustus dan merupakan libur nasional.",
    history:
      "Pada 17 Agustus 1945, Soekarno-Hatta memproklamasikan kemerdekaan Indonesia di Jl. Pegangsaan Timur 56 Jakarta. Tanggal ini menjadi libur nasional terpenting dan diperingati dengan upacara kenegaraan di Istana Merdeka.",
    traditions: [
      "Upacara bendera serentak di seluruh Indonesia.",
      "Lomba 17-an: panjat pinang, balap karung, makan kerupuk.",
      "Pawai obor & peringatan malam takbir kemerdekaan.",
    ],
    relatedSlugs: ["pahlawan", "pancasila", "sumpah-pemuda"],
    iconKey: "kemerdekaan",
    greetings: [
      "Dirgahayu Republik Indonesia! Merdeka!",
      "17 Agustus: bangga menjadi Indonesia, bersatu untuk maju.",
      "Selamat HUT RI - terus berkarya untuk bangsa tercinta.",
      "Sekali merdeka, tetap merdeka. Dirgahayu Indonesia!",
      "Merah putih di dada, Indonesia di hati. Selamat HUT Kemerdekaan!",
    ],
  },
  "sumpah-pemuda": {
    tagline: "Ikrar pemuda Indonesia tahun 1928 yang menyatukan bangsa.",
    quickAnswer: "Hari Sumpah Pemuda diperingati setiap tanggal 28 Oktober.",
    history:
      "Kongres Pemuda II di Jakarta pada 27-28 Oktober 1928 menghasilkan ikrar Sumpah Pemuda: satu tanah air, satu bangsa, satu bahasa - Indonesia. Lagu Indonesia Raya karya WR Supratman juga pertama kali diperdengarkan saat itu.",
    traditions: [
      "Upacara peringatan di sekolah & kampus.",
      "Refleksi nilai persatuan pemuda lintas suku & agama.",
      "Festival kepemudaan & lomba bahasa Indonesia.",
    ],
    relatedSlugs: ["kemerdekaan", "kebangkitan-nasional", "pahlawan"],
    iconKey: "sumpah",
    greetings: [
      "Selamat Hari Sumpah Pemuda! Satu nusa, satu bangsa, satu bahasa - Indonesia.",
      "28 Oktober: pemuda hari ini, pemimpin hari esok.",
      "Jas merah - jangan sekali-kali melupakan sejarah. Selamat Hari Sumpah Pemuda.",
      "Pemuda Indonesia: bersatu, berkarya, bermakna.",
      "Selamat memperingati Hari Sumpah Pemuda - api perjuangan tak boleh padam.",
    ],
  },
  "lahir-pancasila": {
    tagline: "Memperingati pidato Soekarno yang melahirkan Pancasila.",
    quickAnswer: "Hari Lahir Pancasila diperingati setiap tanggal 1 Juni dan merupakan libur nasional sejak 2017.",
    history:
      "Pada 1 Juni 1945, Soekarno menyampaikan pidato di sidang BPUPKI yang memuat lima dasar negara - dikenal sebagai 'lahir'-nya Pancasila. Presiden Joko Widodo menetapkan 1 Juni sebagai libur nasional melalui Keppres No. 24 Tahun 2016.",
    traditions: [
      "Upacara nasional dengan amanat Presiden.",
      "Pembacaan teks Pancasila di sekolah & instansi.",
      "Diskusi penguatan nilai Pancasila lintas generasi.",
    ],
    relatedSlugs: ["kemerdekaan", "kesaktian-pancasila", "pahlawan"],
  },
  "pendidikan-nasional": {
    tagline: "Mengenang Ki Hajar Dewantara, Bapak Pendidikan Nasional.",
    quickAnswer: "Hari Pendidikan Nasional (Hardiknas) diperingati setiap tanggal 2 Mei.",
    history:
      "Tanggal 2 Mei dipilih karena bertepatan dengan kelahiran Ki Hajar Dewantara, pendiri Taman Siswa, pada 2 Mei 1889. Ditetapkan melalui Keppres No. 316 Tahun 1959.",
    figure: {
      name: "Ki Hajar Dewantara",
      role: "Bapak Pendidikan Nasional",
      bio: "Mendirikan Perguruan Taman Siswa pada 1922 yang menjadi cikal bakal pendidikan kerakyatan di Indonesia. Semboyan: Ing Ngarsa Sung Tuladha, Ing Madya Mangun Karsa, Tut Wuri Handayani.",
    },
    traditions: [
      "Upacara di sekolah dengan amanat Mendikbud.",
      "Pemberian penghargaan Anugerah Hardiknas.",
      "Diskusi reformasi pendidikan & merdeka belajar.",
    ],
    relatedSlugs: ["guru", "kartini", "kebangkitan-nasional"],
  },
  valentine: {
    tagline: "Hari Kasih Sayang yang dirayakan di seluruh dunia setiap 14 Februari.",
    quickAnswer: "Hari Valentine diperingati setiap tanggal 14 Februari sebagai hari kasih sayang.",
    history:
      "Berakar dari peringatan Santo Valentinus pada abad ke-3 Masehi, Hari Valentine populer secara global sejak abad ke-19 sebagai momen mengekspresikan cinta dan kasih sayang kepada pasangan, keluarga, dan sahabat.",
    traditions: [
      "Mengirim kartu, bunga, atau cokelat untuk orang terkasih.",
      "Makan malam romantis bersama pasangan.",
      "Memberi apresiasi kepada keluarga & teman dekat.",
    ],
    relatedSlugs: ["perempuan-internasional", "ibu", "anak-sedunia"],
    iconKey: "valentine",
    greetings: [
      "Happy Valentine's Day! Cinta tulus tak butuh momen, tapi hari ini cocok untuk dirayakan.",
      "Untukmu, sumber kebahagiaanku - selamat Hari Kasih Sayang.",
      "Valentine bukan hanya tentang pasangan, tapi semua orang yang kita sayangi.",
      "Sayangi yang ada di samping, hargai yang setia menemani. Happy Valentine!",
    ],
  },
  natal: {
    tagline: "Perayaan kelahiran Yesus Kristus, libur nasional di Indonesia.",
    quickAnswer: "Hari Natal diperingati setiap tanggal 25 Desember dan merupakan libur nasional di Indonesia.",
    history:
      "Hari Natal memperingati kelahiran Yesus Kristus dan dirayakan umat Kristen di seluruh dunia. Di Indonesia, Natal ditetapkan sebagai libur nasional dan biasanya dirangkai dengan masa Adven sebelumnya.",
    traditions: [
      "Ibadah Misa Natal di gereja.",
      "Berkumpul keluarga, tukar kado, makan bersama.",
      "Menghias pohon Natal & menyalakan lilin Adven.",
    ],
    relatedSlugs: ["tahun-baru", "kemerdekaan"],
    iconKey: "natal",
    greetings: [
      "Selamat Hari Natal! Damai di bumi, sukacita di hati.",
      "Merry Christmas - semoga kasih Natal menerangi hari-harimu.",
      "Natal: saatnya berbagi kasih, harapan, dan kehangatan.",
      "Selamat Natal untuk seluruh saudara Kristiani Indonesia.",
    ],
  },
  "tahun-baru": {
    tagline: "Awal tahun Masehi yang dirayakan di seluruh dunia.",
    quickAnswer: "Tahun Baru Masehi diperingati setiap tanggal 1 Januari dan merupakan libur nasional.",
    history:
      "Tahun Baru 1 Januari mengikuti kalender Gregorian yang ditetapkan Paus Gregorius XIII pada 1582 dan kini dipakai sebagai standar internasional. Di Indonesia, malam pergantian tahun dirayakan dengan kembang api dan acara komunitas.",
    traditions: [
      "Countdown menjelang tengah malam.",
      "Pesta kembang api & terompet.",
      "Resolusi tahun baru bersama keluarga.",
    ],
    relatedSlugs: ["natal", "kemerdekaan"],
    greetings: [
      "Selamat Tahun Baru! Semoga tahun ini membawa lebih banyak kebahagiaan.",
      "Happy New Year - mulai dengan langkah baru, semangat baru.",
      "1 Januari: lembaran baru, harapan baru.",
      "Selamat tahun baru, sukses selalu untuk kita semua.",
    ],
  },
  "perempuan-internasional": {
    tagline: "Hari Perempuan Internasional menggemakan kesetaraan dan hak perempuan global.",
    quickAnswer: "Hari Perempuan Internasional (International Women's Day) diperingati setiap tanggal 8 Maret.",
    history:
      "International Women's Day pertama kali diperingati pada 1911 dan diakui PBB sejak 1977. Tema setiap tahun mengangkat isu kesetaraan, hak ekonomi, dan keterwakilan perempuan.",
    traditions: [
      "Aksi & seminar kesetaraan gender.",
      "Kampanye #IWD di media sosial.",
      "Apresiasi figur perempuan inspiratif.",
    ],
    relatedSlugs: ["kartini", "ibu", "ayah"],
    greetings: [
      "Happy International Women's Day! Setara, berdaya, bermakna.",
      "Untuk perempuan tangguh di mana pun: kamu luar biasa.",
      "8 Maret: rayakan setiap perempuan yang membuat dunia lebih baik.",
      "Selamat Hari Perempuan Internasional - terus bersuara, terus berkarya.",
    ],
  },
  "anak-nasional": {
    tagline: "Hari Anak Nasional Indonesia, momentum menjamin hak setiap anak.",
    quickAnswer: "Hari Anak Nasional diperingati setiap tanggal 23 Juli berdasarkan Keppres No. 44 Tahun 1984.",
    history:
      "Tanggal 23 Juli dipilih bertepatan dengan disahkannya UU Kesejahteraan Anak No. 4 Tahun 1979. Hari Anak Nasional menjadi pengingat untuk memenuhi hak hidup, tumbuh kembang, perlindungan, dan partisipasi anak.",
    traditions: [
      "Acara puncak nasional dengan tema tahunan.",
      "Kegiatan kreatif anak: lomba menggambar, festival.",
      "Sosialisasi perlindungan anak & anti kekerasan.",
    ],
    relatedSlugs: ["ibu", "ayah", "anak-sedunia"],
    iconKey: "anak",
    greetings: [
      "Selamat Hari Anak Nasional! Anak bahagia, bangsa berjaya.",
      "Lindungi anak, hormati hak-hak mereka. Selamat Hari Anak Nasional.",
      "Setiap anak istimewa, setiap anak berharga. Selamat Hari Anak!",
      "23 Juli: berikan yang terbaik untuk masa depan generasi penerus.",
    ],
  },
};

export function getHariContent(slug: string): HariContent | undefined {
  return HARI_CONTENT[slug];
}